import React, {useState} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {useUpdateProductionSet} from '../../../hooks/productions/useProductions';
import {IUpdateSetPayload} from '../../../api/types/production.type';
import ArrButton from '../../../components/common/arr-button/ArrButton';
import {ReactComponent as CaretDown} from '../../../assets/images/caret-down.svg';

const UpdateProductionSetApiTest: React.FC = () => {
  const [field, setField] = useState<string>('PP to PP API');
  const [isProductionSet, setIsProductionSet] = useState<boolean>(true);
  const [securityGroupId, setSecurityGroupId] = useState<number>(1);
  const [productionSetId, setProductionSetId] = useState<number>(5);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const updateSetMutation = useUpdateProductionSet(
    (data) => {
      console.log('Production set updated successfully:', data);
    },
    (error) => {
      console.error('Error updating production set:', error);
    }
  );

  const buildPayload = (): IUpdateSetPayload => ({
    field,
    isProductionSet,
    securityGroupId,
    productionSetId,
  });

  const handleTestApi = () => {
    updateSetMutation.mutate(buildPayload());
  };

  const handleReset = () => {
    setField('PP to PP API');
    setIsProductionSet(true);
    setSecurityGroupId(1);
    setProductionSetId(5);
  };

  const copyToClipboard = async (data: unknown) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyPayload = () => copyToClipboard(buildPayload());

  const handleCopyResponse = () => {
    if (updateSetMutation.data) {
      copyToClipboard(updateSetMutation.data);
    }
  };

  return (
    <Box className="demo-page" padding={3}>
      <Typography
        variant="h4"
        className="typo-text headline2_size"
        gutterBottom
      >
        🧪 Update Production Set API Test
      </Typography>
      <Typography
        className="typo-text body3_size"
        sx={{color: '#666', marginBottom: 2}}
      >
        Update the name or metadata for an existing production/export set.
      </Typography>
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          padding: 2,
          borderRadius: 1,
          marginBottom: 3,
        }}
      >
        <Typography variant="h6" className="typo-text subhead2_size">
          Request Payload
        </Typography>

        <Box sx={{display: 'flex', gap: 2, marginTop: 2, flexWrap: 'wrap'}}>
          <TextField
            label="Field"
            type="text"
            value={field}
            onChange={(e) => setField(e.target.value)}
            size="small"
            placeholder="Enter set name"
            sx={{minWidth: '200px'}}
          />
          <TextField
            label="Security Group ID"
            type="number"
            value={securityGroupId}
            onChange={(e) => setSecurityGroupId(Number(e.target.value))}
            size="small"
            placeholder="Enter security group ID"
          />
          <TextField
            label="Production Set ID"
            type="number"
            value={productionSetId}
            onChange={(e) => setProductionSetId(Number(e.target.value))}
            size="small"
            placeholder="Enter production set ID"
          />
          <FormControlLabel
          data-testid="is-production-set"
            control={
              <Switch
                checked={isProductionSet}
                onChange={(event) => setIsProductionSet(event.target.checked)}
                color="primary"
              />
            }
            label="Is Production Set"
          />
        </Box>

        <Box sx={{marginTop: 2, display: 'flex', gap: 2, flexWrap: 'wrap'}}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleTestApi}
            disabled={updateSetMutation.isPending}
            data-testid="update-production-set-api-test-button">
            {updateSetMutation.isPending ? '⏳ Updating...' : '🚀 Update Set'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleReset}
            data-testid="update-production-set-api-test-reset-button">
            🔄 Reset
          </Button>
          <Button
            variant="outlined"
            onClick={handleCopyPayload}
            data-testid="update-production-set-api-test-copy-payload-button">
            📋 Copy Payload
          </Button>
          <Button
            variant="outlined"
            onClick={handleCopyResponse}
            disabled={!updateSetMutation.data}
            data-testid="update-production-set-api-test-copy-response-button">
            📋 Copy Response
          </Button>
        </Box>

        {copySuccess && (
          <Typography sx={{marginTop: 1}} className="typo-text caption1_size">
            ✅ Copied to clipboard!
          </Typography>
        )}
      </Box>
      {updateSetMutation.isPending && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            border: '1px solid #2196f3',
          }}
        >
          <Typography className="typo-text body2_size">
            ⏳ Updating production set... Making API request...
          </Typography>
        </Box>
      )}
      {updateSetMutation.error && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#ffebee',
            borderRadius: 1,
            border: '1px solid #f44336',
          }}
        >
          <Typography className="typo-text subhead3_size" color="error">
            ❌ Error
          </Typography>
          <Typography className="typo-text body3_size" color="error">
            {updateSetMutation.error.message}
          </Typography>
        </Box>
      )}
      {updateSetMutation.data && !updateSetMutation.isPending && (
        <Accordion
          defaultExpanded
          sx={{
            marginTop: 3,
            boxShadow: 2,
            borderRadius: 1,
            '&:before': {display: 'none'},
          }}
        >
          <AccordionSummary
            expandIcon={<CaretDown />}
            sx={{
              backgroundColor: '#f0f7ff',
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <Typography className="typo-text subhead2_size">
              ✅ Response Payload
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              backgroundColor: '#fafafa',
              borderRadius: '0 0 8px 8px',
            }}
          >
            <pre
              style={{
                backgroundColor: '#1e1e1e',
                color: '#d4d4d4',
                padding: '16px',
                borderRadius: '8px',
                overflowX: 'auto',
                margin: 0,
              }}
            >
              {JSON.stringify(updateSetMutation.data, null, 2)}
            </pre>
          </AccordionDetails>
        </Accordion>
      )}
      <Accordion
        sx={{
          marginTop: 3,
          boxShadow: 2,
          borderRadius: 1,
          '&:before': {display: 'none'},
        }}
      >
        <AccordionSummary
          expandIcon={<CaretDown />}
          sx={{
            backgroundColor: '#f0f7ff',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Typography className="typo-text subhead2_size">
            📘 Sample Payload & Response
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            backgroundColor: '#fafafa',
            borderRadius: '0 0 8px 8px',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{color: '#555', marginBottom: 1}}
            className="typo-text subhead3_size"
          >
            Sample Request:
          </Typography>
          <pre
            style={{
              backgroundColor: '#1e1e1e',
              color: '#d4d4d4',
              padding: '16px',
              borderRadius: '8px',
              overflowX: 'auto',
            }}
          >
            {JSON.stringify(buildPayload(), null, 2)}
          </pre>

          <Typography
            variant="subtitle1"
            sx={{color: '#555', marginTop: 2, marginBottom: 1}}
            className="typo-text subhead3_size"
          >
            Sample Response:
          </Typography>
          <pre
            style={{
              backgroundColor: '#1e1e1e',
              color: '#d4d4d4',
              padding: '16px',
              borderRadius: '8px',
              overflowX: 'auto',
            }}
          >
            {`{
  "message": "Production Set has been updated successfully.",
  "success": true,
  "field": "${field}"
}`}
          </pre>
        </AccordionDetails>
      </Accordion>
      <Box sx={{marginTop: 3, display: 'flex', justifyContent: 'flex-end'}}>
        <ArrButton
          variant="contained"
          size="medium"
          label="Test API"
          onClick={handleTestApi}
          isLoading={updateSetMutation.isPending}
          dataTestId="update-production-set-api-test-test-api-button" />
      </Box>
    </Box>
  );
};

export default UpdateProductionSetApiTest;
